import { api, apiUnauthenticated, setTokens, clearTokens, getAccessToken } from './client';
import type { SessionDto, TokenResponse, UserDto } from './types';

/**
 * Устойчивый ID именно этого браузера (не аккаунта!) — хранится в localStorage, переживает
 * логаут/логин. Раньше здесь было 'web-' + username: один и тот же "отпечаток" у ЛЮБОГО
 * браузера, залогиненного под одним аккаунтом. Бэкенд по этому отпечатку переиспользует
 * запись Device (см. AuthService.resolveDevice) — с общим на всех отпечатком вход с телефона
 * СХЛОПЫВАЛСЯ с записью устройства от ПК и перезаписывал её платформу/имя: старая сессия с ПК
 * задним числом становилась "Android", а в списке сессий все устройства выглядели дублями
 * одного и того же. Отпечаток должен различать браузеры, а не аккаунты (бэкенд и так
 * дополнительно скопирован по userId — на одном браузере с разных аккаунтов записи не смешаются).
 */
function getDeviceFingerprint(): string {
  const KEY = 'hc_device_fp';
  let fp = localStorage.getItem(KEY);
  if (!fp) {
    fp = 'web-' + crypto.randomUUID();
    localStorage.setItem(KEY, fp);
  }
  return fp;
}

/** Грубый разбор браузера/ОС из User-Agent — только для отображения пользователю
 *  в списке сессий ("с какого устройства"), не для каких-либо решений безопасности. */
function detectClientInfo(): { name: string; platform: string; appVersion?: string } {
  const ua = navigator.userAgent;
  let browser = 'Браузер'; // i18n-ok
  let version = '';
  if (/Edg\//.test(ua)) {
    browser = 'Edge';
    version = ua.match(/Edg\/([\d.]+)/)?.[1] ?? '';
  } else if (/OPR\//.test(ua)) {
    browser = 'Opera';
    version = ua.match(/OPR\/([\d.]+)/)?.[1] ?? '';
  } else if (/Chrome\//.test(ua)) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/([\d.]+)/)?.[1] ?? '';
  } else if (/Firefox\//.test(ua)) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/([\d.]+)/)?.[1] ?? '';
  } else if (/Version\//.test(ua) && /Safari\//.test(ua)) {
    browser = 'Safari';
    version = ua.match(/Version\/([\d.]+)/)?.[1] ?? '';
  }

  let platform = 'Web';
  if (/Windows/.test(ua)) platform = 'Windows';
  else if (/Mac OS X/.test(ua)) platform = 'macOS';
  else if (/Android/.test(ua)) platform = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) platform = 'iOS';
  else if (/Linux/.test(ua)) platform = 'Linux';

  return {
    name: `${browser} · ${platform}`,
    platform,
    appVersion: version ? `${browser} ${version}` : undefined,
  };
}

/** Оба поля опциональны по отдельности, но хотя бы одно обязано быть заполнено (проверка — на вызывающей стороне и на сервере). */
export interface RegisterContact {
  email?: string;
  phone?: string;
}

export async function register(username: string, displayName: string, password: string, contact: RegisterContact) {
  return apiUnauthenticated<UserDto>('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify({
      username,
      displayName,
      password,
      email: contact.email || undefined,
      phone: contact.phone || undefined,
    }),
  });
}

export interface UsernameAvailability {
  available: boolean;
  suggestions: string[];
}

export function checkUsernameAvailable(username: string, displayName?: string): Promise<UsernameAvailability> {
  const q = new URLSearchParams({ username });
  if (displayName) q.set('displayName', displayName);
  return apiUnauthenticated<UsernameAvailability>(`/api/v1/auth/username-available?${q}`);
}

/** Вход по логину/телефону/email. При включённой 2FA сервер отвечает 428 (Precondition
 *  Required) — тогда нужно повторить запрос с code из Google Authenticator. 428 не сохраняет
 *  токены: setTokens вызывается только на успехе. */
export async function login(identifier: string, password: string, code?: string): Promise<TokenResponse> {
  const info = detectClientInfo();
  const res = await apiUnauthenticated<TokenResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      username: identifier,
      password,
      code: code || undefined,
      device: { ...info, fingerprint: getDeviceFingerprint() },
    }),
  });
  setTokens(res.accessToken, res.refreshToken);
  return res;
}

// ---------- QR-вход (см. backend QrAuthService) ----------

export interface QrStart {
  qrToken: string;
  /** Секрет этого браузера для poll — в QR не кладётся. */
  authCode: string;
  expiresIn: number;
}

export interface QrInfo {
  deviceName: string;
  platform: string;
  ip?: string | null;
  expiresIn: number;
}

export function qrStart(): Promise<QrStart> {
  const info = detectClientInfo();
  return apiUnauthenticated<QrStart>('/api/v1/auth/qr/start', {
    method: 'POST',
    body: JSON.stringify({ device: { ...info, fingerprint: getDeviceFingerprint() } }),
  });
}

/** Содержимое QR: сканирует телефон (камерой в приложении или системной камерой). */
export function qrPayload(qrToken: string): string {
  return `homechat://auth?t=${encodeURIComponent(qrToken)}`;
}

/** Один шаг поллинга. При confirmed токены сохраняются сразу (как в login). */
export async function qrPoll(start: QrStart): Promise<'pending' | 'confirmed' | 'expired'> {
  const res = await apiUnauthenticated<{ status: 'pending' | 'confirmed' | 'expired'; tokens?: TokenResponse | null }>(
    '/api/v1/auth/qr/poll',
    { method: 'POST', body: JSON.stringify({ qrToken: start.qrToken, authCode: start.authCode }) },
  );
  if (res.status === 'confirmed' && res.tokens) setTokens(res.tokens.accessToken, res.tokens.refreshToken);
  return res.status;
}

/** Достаёт qrToken из ссылки homechat://auth?t=... (или принимает «голый» токен). */
export function parseQrToken(text: string): string | null {
  const s = text.trim();
  const m = s.match(/[?&]t=([^&\s]+)/);
  if (m) return decodeURIComponent(m[1]);
  return /^[A-Za-z0-9_-]{16,64}$/.test(s) ? s : null;
}

export function qrInfo(qrToken: string): Promise<QrInfo> {
  return api<QrInfo>('/api/v1/auth/qr/info', { method: 'POST', body: JSON.stringify({ qrToken }) });
}

export function qrConfirm(qrToken: string) {
  return api('/api/v1/auth/qr/confirm', { method: 'POST', body: JSON.stringify({ qrToken }) });
}

// ---------- 2FA (Google Authenticator) ----------

export function twoFactorSetup(): Promise<{ secret: string; otpauthUri: string }> {
  return api('/api/v1/auth/2fa/setup');
}

export function twoFactorEnable(code: string) {
  return api('/api/v1/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) });
}

export function twoFactorDisable(code: string) {
  return api('/api/v1/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) });
}

export async function logout() {
  const access = getAccessToken();
  try {
    if (access) await api('/api/v1/auth/logout', { method: 'POST' });
  } finally {
    clearTokens();
  }
}

export async function me(): Promise<UserDto> {
  return api<UserDto>('/api/v1/users/me');
}

export function listSessions(): Promise<SessionDto[]> {
  return api<SessionDto[]>('/api/v1/auth/sessions');
}

export function revokeSession(id: string) {
  return api(`/api/v1/auth/sessions/${id}`, { method: 'DELETE' });
}

/** Завершить все сессии, кроме текущей. */
export function revokeOtherSessions() {
  return api('/api/v1/auth/sessions/others', { method: 'DELETE' });
}
